import React, { useState, useEffect } from 'react';
import {
  ExclamationTriangleIcon,
  XMarkIcon,
  TrashIcon,
  ArchiveBoxIcon,
  InformationCircleIcon,
  FolderIcon
} from '@heroicons/react/24/outline';

export default function TaxonomyDeleteModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  item, 
  usageData,
  loading 
}) {
  const [confirmText, setConfirmText] = useState('');
  const [deleteType, setDeleteType] = useState('archive'); // 'archive' or 'delete'

  useEffect(() => {
    if (!isOpen) {
      setConfirmText('');
      setDeleteType('archive');
    }
  }, [isOpen]);

  if (!isOpen || !item) return null;

  console.log('Delete modal item:', item);
  
  const hasUsage = usageData && usageData.totalCount > 0;
  const expectedText = item.label || item.key || 'DELETE';
  const canDelete = confirmText === expectedText && (!hasUsage || deleteType === 'archive');
  
  console.log('Expected text for confirmation:', expectedText);

  const handleConfirm = () => {
    if (canDelete) {
      onConfirm(deleteType);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="w-6 h-6 text-orange-500 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900">
              Delete "{item.label}"
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Usage Warning */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              <span className="ml-3 text-gray-600">Checking usage...</span>
            </div>
          ) : hasUsage ? (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-orange-800 mb-2">
                    ⚠️ This item is currently being used
                  </h4>
                  <p className="text-sm text-orange-700 mb-3">
                    "{item.label}" is referenced in <strong>{usageData.totalCount}</strong> project(s):
                  </p>
                  
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {usageData.projects.slice(0, 10).map((project, index) => (
                      <div key={index} className="bg-white rounded p-2 text-sm">
                        <div className="font-medium text-gray-900">{project.name}</div>
                        <div className="text-gray-600">
                          Customer: {project.customerName} | Status: {project.status}
                        </div>
                        <div className="text-orange-600 text-xs">{project.context}</div>
                      </div>
                    ))}
                    {usageData.projects.length > 10 && (
                      <div className="text-sm text-orange-600">
                        +{usageData.projects.length - 10} more projects...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <InformationCircleIcon className="w-5 h-5 text-green-500 mr-3" />
                <div className="text-sm text-green-700">
                  ✅ This item is not currently being used in any projects and can be safely deleted.
                </div>
              </div>
            </div>
          )}

          {/* Delete Options */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">How would you like to proceed?</h4>
            
            <div className="space-y-3">
              {/* Archive Option */}
              <label className="flex items-start cursor-pointer">
                <input
                  type="radio"
                  name="deleteType"
                  value="archive"
                  checked={deleteType === 'archive'}
                  onChange={(e) => setDeleteType(e.target.value)}
                  className="mt-1 text-orange-600 focus:ring-orange-500"
                />
                <div className="ml-3">
                  <div className="flex items-center">
                    <ArchiveBoxIcon className="w-4 h-4 text-orange-600 mr-2" />
                    <span className="font-medium text-gray-900">Archive (Recommended)</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Hide from new selections but preserve existing project data. This is the safest option.
                  </p>
                </div>
              </label>

              {/* Delete Option */}
              <label className="flex items-start cursor-pointer">
                <input
                  type="radio"
                  name="deleteType"
                  value="delete"
                  checked={deleteType === 'delete'}
                  onChange={(e) => setDeleteType(e.target.value)}
                  className="mt-1 text-red-600 focus:ring-red-500"
                  disabled={hasUsage}
                />
                <div className="ml-3">
                  <div className="flex items-center">
                    <TrashIcon className="w-4 h-4 text-red-600 mr-2" />
                    <span className={`font-medium ${hasUsage ? 'text-gray-400' : 'text-gray-900'}`}>
                      Permanently Delete
                    </span>
                  </div>
                  <p className={`text-sm mt-1 ${hasUsage ? 'text-gray-400' : 'text-gray-600'}`}>
                    {hasUsage 
                      ? '❌ Cannot delete - item is currently in use'
                      : 'Completely remove from database. Cannot be undone.'
                    }
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Confirmation Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Type "{item.label || item.key || 'DELETE'}" to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder={item.label || item.key || 'DELETE'}
            />
          </div>

          {/* Item Details */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="font-medium text-gray-900 mb-2">Item Details:</h5>
            <div className="space-y-1 text-sm text-gray-600">
              <div>Type: {item.type}</div>
              <div>Path: {item.path}</div>
              {item.description && <div>Description: {item.description}</div>}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canDelete}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              canDelete
                ? deleteType === 'archive'
                  ? 'bg-orange-600 text-white hover:bg-orange-700'
                  : 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {deleteType === 'archive' ? 'Archive Item' : 'Delete Permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}